import { useSessionActivity } from '@/hooks/useSessionActivity';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  MessageSquare, 
  TrendingUp, 
  UserPlus, 
  FileCheck, 
  CheckCircle,
  Loader2,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SessionActivityTimelineProps {
  sessionId: string;
  workspaceId: string;
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'comment':
      return <MessageSquare className="h-4 w-4" />;
    case 'phase_change':
      return <TrendingUp className="h-4 w-4" />;
    case 'member_joined':
      return <UserPlus className="h-4 w-4" />;
    case 'decision_created':
      return <FileCheck className="h-4 w-4" />;
    case 'step_completed':
      return <CheckCircle className="h-4 w-4" />;
    default:
      return <Clock className="h-4 w-4" />;
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
    case 'comment':
      return 'bg-blue-500/10 text-blue-500';
    case 'phase_change':
      return 'bg-green-500/10 text-green-500';
    case 'member_joined':
      return 'bg-purple-500/10 text-purple-500';
    case 'decision_created':
      return 'bg-primary/10 text-primary';
    case 'step_completed':
      return 'bg-emerald-500/10 text-emerald-500';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const SessionActivityTimeline = ({ sessionId, workspaceId }: SessionActivityTimelineProps) => {
  const { activities, loading } = useSessionActivity(sessionId, workspaceId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Aktivitäts-Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Noch keine Aktivitäten
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[21px] top-0 bottom-0 w-[2px] bg-border" />
              
              <div className="space-y-6">
                {activities.map((activity, index) => (
                  <div key={activity.id} className="relative flex gap-4">
                    {/* Timeline dot with icon */}
                    <div className={cn(
                      "relative z-10 flex items-center justify-center h-10 w-10 rounded-full border-2 border-background flex-shrink-0",
                      getActivityColor(activity.type)
                    )}>
                      {getActivityIcon(activity.type)}
                    </div>

                    {/* Activity content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {activity.user_avatar && (
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={activity.user_avatar} />
                                <AvatarFallback className="text-xs">
                                  {activity.user_name?.charAt(0)?.toUpperCase() || '?'}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <span className="font-semibold text-sm">
                              {activity.title}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {activity.description}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDistanceToNow(new Date(activity.created_at), {
                            locale: de,
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                      {activity.user_name && (
                        <p className="text-xs text-muted-foreground">
                          von {activity.user_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
